import {
    getConversationCallPreviewText,
    getConversationCallTimestamp,
} from '@apps/web/components/shared/messages/conversation_call_utils';
import { type OrganizationConversationMetadata } from '@libraries/typescript/api_client';

export type ConversationPreview = {
    readonly text: string;
    readonly kind:
        | 'call'
        | 'incoming_message'
        | 'outgoing_message'
        | 'search_match';
    readonly callDirection?: 'inbound' | 'outbound';
};

export function compareConversationMetadata(
    a: OrganizationConversationMetadata,
    b: OrganizationConversationMetadata,
    sort?: 'pending_first',
): number {
    if (sort === 'pending_first') {
        const aPendingFirst = a.pending_staged_message_count > 0 ? 0 : 1;
        const bPendingFirst = b.pending_staged_message_count > 0 ? 0 : 1;
        if (aPendingFirst !== bPendingFirst) {
            return aPendingFirst - bPendingFirst;
        }
    }

    return (
        new Date(b.last_active_at).getTime() -
        new Date(a.last_active_at).getTime()
    );
}

export function getConversationPreview(
    conversation: OrganizationConversationMetadata,
): ConversationPreview | null {
    // During a content search the server includes an excerpt of the matched
    // message; showing it in place of the latest-activity preview tells the
    // user why the conversation is in the results.
    const searchMatchText = conversation.search_match_snippet
        ?.replace(/\s+/g, ' ')
        .trim();
    if (searchMatchText) {
        return { text: searchMatchText, kind: 'search_match' };
    }

    const lastMessage = conversation.last_message;
    const lastMessageText = lastMessage?.content.replace(/\s+/g, ' ').trim();
    const lastCallText = getConversationCallPreviewText(conversation.last_call);

    const lastMessageTimestampMs = lastMessage
        ? new Date(lastMessage.created_at).getTime()
        : Number.NEGATIVE_INFINITY;
    const lastCallTimestampMs = conversation.last_call
        ? new Date(
              getConversationCallTimestamp(conversation.last_call),
          ).getTime()
        : Number.NEGATIVE_INFINITY;

    if (lastCallText && lastCallTimestampMs > lastMessageTimestampMs) {
        return {
            text: lastCallText,
            kind: 'call',
            callDirection: conversation.last_call?.direction,
        };
    }

    if (lastMessageText) {
        return {
            text: lastMessageText,
            kind:
                lastMessage?.role === 'assistant'
                    ? 'outgoing_message'
                    : 'incoming_message',
        };
    }

    if (lastCallText) {
        return {
            text: lastCallText,
            kind: 'call',
            callDirection: conversation.last_call?.direction,
        };
    }

    return null;
}
